import { ZipSelectorForm } from "@app/components/forms";
import { StepperContent } from "@app/components/ui/layout";
import { BloodDrawContext } from "@app/provider";
import { defineMessages } from "react-intl";
import { StepperContext } from "@natera/stepper";
import React from "react";
import { useIntl } from "react-intl";
import { SpinnerView } from "@app/components";
import { Button } from "@natera/form";
import { ZipValidationErrors } from "@app/components/forms";
import { DEFAULT_DISTANCE_MI } from "@app/provider/bloodDraw";

const messages = defineMessages({
  sampleDrawLabTitle: {
    id: "sampleDrawLabTitle",
    defaultMessage: "Lab Locator",
  },
  labCollectionZipSelectorFind: {
    id: "labCollectionZipSelectorFind",
    defaultMessage: "Choose a location to get your blood drawn.",
  },
  labCollectionZipSelectorLocation: {
    id: "labCollectionZipSelectorLocation",
    defaultMessage:
      "Please enter your zip code to find nearby labs. \n Labs within 100 miles will be shown.",
  },
  labCollectionZipSelectorShow: {
    id: "labCollectionZipSelectorShow",
    defaultMessage: "Show labs near me",
  },
  labCollectionZipSelectorBack: {
    id: "labCollectionZipSelectorBack",
    defaultMessage: "Back",
  },
});

export interface ZipSelectorStepProps {
  /**
   * Optional ZIP to pre-populate the form with (from patient profile in
   * the logged-in / guest flows). When present, submitting still runs
   * validation and fetches labs.
   */
  prefillZip?: string;
}

const ZipSelectorStep: React.FC<ZipSelectorStepProps> = ({ prefillZip }) => {
  const intl = useIntl();

  const { goBack, resolve } = React.useContext(StepperContext);
  const { getBloodDrawSites, isLoading, setZipCode } =
    React.useContext(BloodDrawContext);

  const [zipValidationErrorKey, setZipValidationErrorKey] =
    React.useState<ZipValidationErrors | null>(null);

  const onSubmit = async (zipCode: string) => {
    // Push the submitted ZIP into the shared context so LabList can
    // re-fetch when the user changes the distance selector.
    setZipCode(zipCode);
    // Kick off the fetch to warm state, then always advance. LabList's own
    // useLabLocator will surface both empty-result and fetch-failed states
    // in its centered message slot. The under-input hint on this screen is
    // reserved for ZIP-format validation only (INVALID_ZIP_CODE / EMPTY),
    // which ZipSelectorForm handles before we ever reach this callback.
    await getBloodDrawSites({
      location: zipCode,
      distance: DEFAULT_DISTANCE_MI,
    });
    resolve({});
  };

  return (
    <div>
      <StepperContent
        title={intl.formatMessage(messages.sampleDrawLabTitle)}
        subTitle={intl.formatMessage(messages.labCollectionZipSelectorFind)}
        description={intl.formatMessage(
          messages.labCollectionZipSelectorLocation
        )}
      >
        <SpinnerView isLoading={isLoading} />
        <ZipSelectorForm
          onSubmit={onSubmit}
          zipValidationErrorKey={zipValidationErrorKey}
          setZipValidationErrorKey={setZipValidationErrorKey}
          defaultZipCode={prefillZip}
          actions={
            <>
              <Button type="submit" unelevated data-testid="submit">
                {intl.formatMessage(messages.labCollectionZipSelectorShow)}
              </Button>

              <Button onClick={goBack} type="button" data-testid="cancel">
                {intl.formatMessage(messages.labCollectionZipSelectorBack)}
              </Button>
            </>
          }
        />
      </StepperContent>
    </div>
  );
};

export default ZipSelectorStep;
